import { ApolloClient, ApolloProvider, InMemoryCache, NormalizedCacheObject } from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";
import { createContext, useContext, useMemo, useState } from "react";
import { LOCAL_STORAGE_KEY } from "../../models/local-storage-keys";
import { i18n } from "../../translations/i18n";
import { config } from "../../utils/config";

export enum APOLLO_CLIENT_NAME {
  SMARTMELTS_API = "SMARTMELTS_API",
}

interface GraphQLContextType {
  client: ApolloClient<NormalizedCacheObject>;
  setAuthorizationHeader: (token: string | null) => void;
}

const GraphQLContext = createContext<GraphQLContextType>({} as GraphQLContextType);

export const GraphQLProvider = (props: any) => {
  const value = useGraphQLProvider();

  return (
    <GraphQLContext.Provider value={value}>
      <ApolloProvider client={value.client}>{props.children}</ApolloProvider>
    </GraphQLContext.Provider>
  );
};

const useGraphQLProvider = (): GraphQLContextType => {
  const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN));
  const setAuthorizationHeader = (token: string | null) => {
    setAccessToken(token);
  };

  const client = useMemo(() => generateGraphqlClient(accessToken), [accessToken]);

  return { client, setAuthorizationHeader };
};

const generateGraphqlClient = (token: string | null) => {
  if (token) {
    localStorage.setItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN, token);
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
  }

  const smartmeltsApiLink = createUploadLink({
    uri: config().env("REACT_APP_GRAPHQL_URL"),
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
          "accept-language": i18n.language,
        }
      : {},
  });

  return new ApolloClient({
    link: smartmeltsApiLink,
    cache: new InMemoryCache(),
  });
};

export const useGraphQL = () => {
  return useContext(GraphQLContext);
};
